import React, { useState, useEffect } from 'react';
import { weeklyLockInService, WeeklyLockingWithTalents, TalentSelection, ProgressSummary, LockedTalent } from '../services/weeklyLockInService';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import TalentSelectionModal from './TalentSelectionModal';
import WeeklyReportModal from './WeeklyReportModal';
import ActivityReportingModal from './ActivityReportingModal';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  LinearProgress,
  Chip,
  Button,
  IconButton,
  Container,
} from '@mui/material';
import {
  LocalFireDepartment,
  EmojiEvents,
  CalendarMonth,
  TrendingUp,
  Adjust,
  RestartAlt,
  AccessTime,
  FitnessCenter,
  SelfImprovement,
  Add,
  CheckCircle,
  RadioButtonUnchecked,
  EventNote,
  Timer,
  Bedtime,
  Favorite,
  Healing,
  DirectionsRun,
  Science,
  Grass,
  SetMeal,
  Schedule,
  Nature,
  OpenWith,
  Air,
  Spa,
  WaterDrop,
  Psychology,
  EmojiObjects,
  LocalFlorist,
} from '@mui/icons-material';

interface WeeklyLockInDashboardProps {
  userId: string;
}

interface StreakCardProps {
  icon: React.ReactNode;
  title: string;
  value: number;
  description: string;
  gradientColors: [string, string];
  iconColor: string;
}

const StreakCard: React.FC<StreakCardProps> = ({
  icon,
  title,
  value,
  description,
  gradientColors,
  iconColor,
}) => {
  return (
    <Card 
      sx={{
        position: 'relative',
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        transform: 'scale(0.8)',
        '&:hover': {
          transform: 'scale(0.8) translateY(-4px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
        transition: 'all 0.3s ease-in-out',
        background: `linear-gradient(135deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`,
      }}
    >
      {/* Subtle overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 0,
        }}
      />
      
      <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
        {/* Icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: iconColor,
            }}
          >
            {icon}
          </Avatar>
        </Box>

        {/* Content */}
        <Box>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800,
              fontSize: '3rem',
              lineHeight: 1,
              color: 'white',
              mb: 1,
            }}
          >
            {value}
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: 'white',
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              opacity: 0.95,
            }}
          >
            {description}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

interface WeekOverviewCardProps {
  startDate: string;
  endDate: string;
  status: string;
  timeRemaining: string;
}

const WeekOverviewCard: React.FC<WeekOverviewCardProps> = ({
  startDate,
  endDate,
  status,
  timeRemaining,
}) => {
  const getGradientColors = (status: string): [string, string] => {
    if (status === 'active') return ['#10b981', '#059669'];
    if (status === 'completed') return ['#3b82f6', '#1d4ed8'];
    return ['#f59e0b', '#d97706'];
  };

  return (
    <Card 
      sx={{
        position: 'relative',
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        transform: 'scale(0.8)',
        '&:hover': {
          transform: 'scale(0.8) translateY(-4px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
        transition: 'all 0.3s ease-in-out',
        background: `linear-gradient(135deg, ${getGradientColors(status)[0]} 0%, ${getGradientColors(status)[1]} 100%)`,
      }}
    >
      {/* Subtle overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 0,
        }}
      />
      
      <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <EventNote sx={{ color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                Current Week
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                {startDate} - {endDate}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Timer sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'white' }}>
            {timeRemaining}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

interface OverallProgressCardProps {
  percentage: number;
  completedMinutes: number;
  totalMinutes: number;
}

const OverallProgressCard: React.FC<OverallProgressCardProps> = ({
  percentage,
  completedMinutes,
  totalMinutes,
}) => {
  const getGradientColors = (percentage: number): [string, string] => {
    if (percentage >= 80) return ['#10b981', '#059669'];
    if (percentage >= 60) return ['#f59e0b', '#d97706'];
    if (percentage >= 40) return ['#f97316', '#ea580c'];
    return ['#ef4444', '#dc2626'];
  };

  return (
    <Card 
      sx={{
        position: 'relative',
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        transform: 'scale(0.8)',
        '&:hover': {
          transform: 'scale(0.8) translateY(-4px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
        transition: 'all 0.3s ease-in-out',
        background: `linear-gradient(135deg, ${getGradientColors(percentage)[0]} 0%, ${getGradientColors(percentage)[1]} 100%)`,
      }}
    >
      {/* Subtle overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 0,
        }}
      />
      
      <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <TrendingUp sx={{ color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
              Overall Progress
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Weekly completion status
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', mb: 2 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                color: 'white',
                fontSize: '2.5rem',
              }}
            >
              {percentage.toFixed(0)}%
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {completedMinutes}/{totalMinutes} min
            </Typography>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={Math.min(percentage, 100)}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(255, 255, 255, 0.3)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 4,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

interface TalentProgressCardProps {
  talent: ProgressSummary;
}

const TalentProgressCard: React.FC<TalentProgressCardProps> = ({ talent }) => {
  const getGradientColors = (percentage: number): [string, string] => {
    if (percentage >= 80) return ['#10b981', '#059669'];
    if (percentage >= 50) return ['#f59e0b', '#d97706'];
    return ['#ef4444', '#dc2626'];
  };

  const getStatusIcon = (percentage: number) => {
    if (percentage >= 100) return <CheckCircle sx={{ color: 'white' }} />;
    if (percentage >= 50) return <Adjust sx={{ color: 'white' }} />;
    return <RadioButtonUnchecked sx={{ color: 'white' }} />;
  };

  const getStatusText = (percentage: number) => {
    if (percentage >= 100) return 'Completed';
    if (percentage >= 80) return 'On Track';
    if (percentage >= 50) return 'In Progress';
    return 'Behind';
  };

  return (
    <Card 
      sx={{
        position: 'relative',
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
        transition: 'all 0.3s ease-in-out',
        background: `linear-gradient(135deg, ${getGradientColors(talent.completion_percentage)[0]} 0%, ${getGradientColors(talent.completion_percentage)[1]} 100%)`,
      }}
    >
      {/* Subtle overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 0,
        }}
      />
      
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1rem' }}>
            {talent.talent_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(talent.completion_percentage)}
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600, 
                color: 'white',
                fontSize: '0.75rem',
              }}
            >
              {getStatusText(talent.completion_percentage)}
            </Typography>
          </Box>
        </Box>
        
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', mb: 2 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: 'white',
                fontSize: '1.8rem',
              }}
            >
              {talent.completion_percentage.toFixed(0)}%
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.8rem' }}>
              {talent.completed_minutes}/{talent.target_minutes} min
            </Typography>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={Math.min(talent.completion_percentage, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'rgba(255, 255, 255, 0.3)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 3,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// New interface for individual weekly talent progress cards
interface WeeklyTalentProgressCardProps {
  talent: LockedTalent;
  progressSummary: ProgressSummary | undefined;
  timePerRank: number;
  maxRanks: number;
  onReportActivity: (talentId: string) => void;
}

// Talent Icons mapping (from TalentTree.tsx)
const TALENT_ICONS: Record<string, React.ReactNode> = {
  'longevity-start': <Bedtime />, // Sleep Hygiene
  'longevity-cellular': <Favorite />, // Zone Two Cardio
  'longevity-metabolic': <FitnessCenter />, // Strength Training
  'longevity-advanced': <Healing />, // Oral Health
  'longevity-zone-five': <DirectionsRun />, // Zone Five Cardio
  'longevity-creatine': <Science />, // Creatine Supplementation
  'longevity-energy-balance': <TrendingUp />, // Energy Balance
  'longevity-fiber-intake': <Grass />, // Fiber Intake
  'longevity-protein': <SetMeal />, // Protein Optimization
  'longevity-time-restricted-eating': <Schedule />, // Time Restricted Eating
  'longevity-whole-food-eating': <Nature />, // Whole Food, Nutrient-Dense Eating
  'healthspan-cardio': <SelfImprovement />, // Stability Mastery
  'healthspan-fitness': <OpenWith />, // Functional Mobility
  'healthspan-flexibility': <Air />, // Elastic Edge
  'healthspan-recovery': <Spa />, // Recovery Mastery
  'healthspan-alcohol': <WaterDrop />, // Alcohol Elimination
  'healthspan-micronutrients': <Science />, // Micronutrients Sufficiency
  'wellness-mindset': <Psychology />, // Morning Sun Seeker
  'wellness-breathwork': <Air />, // Guided Breath Work
  'wellness-social': <Favorite />, // Social Connection
  'wellness-purpose': <EmojiObjects />, // Purpose & Meaning
  'wellness-stress': <Spa />, // Emotional Regulation
  'wellness-skincare': <LocalFlorist />, // Skin and Hair Maintenance
};

// Tree colors from talent tree
const TREE_COLORS = {
  longevity: '#9c27b0',
  healthspan: '#00bcd4',  
  wellness: '#4caf50',
};

const WeeklyTalentProgressCard: React.FC<WeeklyTalentProgressCardProps> = ({ 
  talent, 
  progressSummary,
  timePerRank,
  maxRanks,
  onReportActivity 
}) => {
  // Calculate ranks locked in for the week
  const lockedRanks = timePerRank > 0 ? Math.round(talent.target_minutes_per_week / timePerRank) : 1;
  
  // Get total minutes completed this week
  const totalCompletedMinutes = progressSummary?.completed_minutes || 0;
  const completionPercentage = progressSummary?.completion_percentage || 0;
  
  // Get number of days with reported activity
  const activeDays = progressSummary?.daily_progress.filter(day => day.minutes > 0).length || 0;
  
  // Get tree color
  const treeColor = TREE_COLORS[talent.tree_type as keyof typeof TREE_COLORS] || '#6b7280';
  
  const getGradientColors = (treeType: string): [string, string] => {
    switch (treeType) {
      case 'longevity':
        return ['#9c27b0', '#ba68c8'];
      case 'healthspan':
        return ['#00bcd4', '#4dd0e1'];
      case 'wellness':
        return ['#4caf50', '#81c784'];
      default:
        return ['#6b7280', '#9ca3af'];
    }
  };

  const getTalentIcon = (talentId: string) => {
    return TALENT_ICONS[talentId] || <SelfImprovement />;
  };

  return (
    <Card 
      sx={{
        position: 'relative',
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
        transition: 'all 0.3s ease-in-out',
        background: `linear-gradient(135deg, ${getGradientColors(talent.tree_type)[0]} 0%, ${getGradientColors(talent.tree_type)[1]} 100%)`,
      }}
    >
      {/* Subtle overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 0,
        }}
      />
      
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        {/* Header with talent icon and plus button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                width: 32,
                height: 32,
              }}
            >
              {getTalentIcon(talent.talent_id)}
            </Avatar>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                textTransform: 'capitalize',
                fontWeight: 600,
              }}
            >
              {talent.tree_type}
            </Typography>
          </Box>
          <IconButton
            onClick={() => onReportActivity(talent.talent_id)}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              width: 36,
              height: 36,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                transform: 'scale(1.1)',
              },
            }}
          >
            <Add />
          </IconButton>
        </Box>

        {/* Talent name */}
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: 'white', 
            mb: 2,
            fontSize: '1.1rem',
            lineHeight: 1.2,
          }}
        >
          {talent.talent_name}
        </Typography>

        {/* Stats grid */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'white',
                  fontSize: '1.8rem',
                }}
              >
                {lockedRanks}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.75rem',
                }}
              >
                Ranks Locked
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'white',
                  fontSize: '1.8rem',
                }}
              >
                {talent.target_minutes_per_week}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.75rem',
                }}
              >
                Minutes/Week
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Progress stats */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.85rem',
              }}
            >
              Progress: {totalCompletedMinutes}/{talent.target_minutes_per_week} min
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'white',
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
            >
              {Math.round(completionPercentage)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(completionPercentage, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'rgba(255, 255, 255, 0.3)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {/* Active days indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CheckCircle sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '1rem' }} />
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.8rem',
            }}
          >
            {activeDays} active days this week
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// Weekly Stats Cards Component
interface WeeklyStatsCardsProps {
  locking: WeeklyLockingWithTalents;
  progressSummary: ProgressSummary[];
}

const WeeklyStatsCards: React.FC<WeeklyStatsCardsProps> = ({ locking, progressSummary }) => {
  const totalTarget = locking.total_time_minutes;
  const totalCompleted = progressSummary.reduce((sum, talent) => sum + talent.completed_minutes, 0);
  const overallCompletion = totalTarget > 0 ? (totalCompleted / totalTarget) * 100 : 0;
  
  const daysWithProgress = progressSummary.length > 0 
    ? progressSummary.reduce((days, talent) => {
        const activeDays = talent.daily_progress.filter(day => day.minutes > 0).length;
        return Math.max(days, activeDays);
      }, 0)
    : 0;
  
  const averageDailyMinutes = progressSummary.length > 0 
    ? progressSummary.reduce((sum, talent) => {
        return sum + talent.daily_progress.reduce((daySum, day) => daySum + day.minutes, 0);
      }, 0) / 7
    : 0;

  const stats = [
    {
      title: 'Overall Completion',
      value: `${Math.round(overallCompletion)}%`,
      gradient: ['#3b82f6', '#2563eb'],
      icon: <TrendingUp />,
    },
    {
      title: 'Minutes Completed',
      value: totalCompleted.toString(),
      gradient: ['#10b981', '#059669'],
      icon: <Timer />,
    },
    {
      title: 'Active Days',
      value: daysWithProgress.toString(),
      gradient: ['#8b5cf6', '#7c3aed'],
      icon: <EventNote />,
    },
    {
      title: 'Avg Daily Minutes',
      value: Math.round(averageDailyMinutes).toString(),
      gradient: ['#f59e0b', '#d97706'],
      icon: <AccessTime />,
    },
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {stats.map((stat, index) => (
        <Grid item xs={6} md={3} key={index}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${stat.gradient[0]} 0%, ${stat.gradient[1]} 100%)`,
              borderRadius: 3,
              overflow: 'hidden',
              position: 'relative',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255, 255, 255, 0.1)',
                zIndex: 0,
              }}
            />
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1, textAlign: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                {stat.icon}
              </Avatar>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: '2.5rem',
                  lineHeight: 1,
                  color: 'white',
                  mb: 1,
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 500,
                }}
              >
                {stat.title}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// Daily Timeline Card Component
interface DailyTimelineCardProps {
  locking: WeeklyLockingWithTalents;
  progressSummary: ProgressSummary[];
}

const DailyTimelineCard: React.FC<DailyTimelineCardProps> = ({ locking, progressSummary }) => {
  const generateWeekProgress = () => {
    const startDate = new Date(locking.week_start_date);
    const endDate = new Date(locking.week_end_date);
    const today = new Date();
    const dailyTarget = Math.round(locking.total_time_minutes / 7);
    
    const weekDays = [];
    
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      const dateString = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      
      const dayMinutes = progressSummary.reduce((total, talent) => {
        const dayProgress = talent.daily_progress.find(p => p.date === dateString);
        return total + (dayProgress?.minutes || 0);
      }, 0);
      
      const completionPercentage = dailyTarget > 0 ? (dayMinutes / dailyTarget) * 100 : 0;
      
      weekDays.push({
        date: dateString,
        dayName,
        isToday: dateString === today.toISOString().split('T')[0],
        totalMinutes: dayMinutes,
        targetMinutes: dailyTarget,
        completionPercentage
      });
    }
    
    return weekDays;
  };

  const weekProgress = generateWeekProgress();

  return (
    <Card
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        mb: 4,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 0,
        }}
      />
      <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'white',
            mb: 3,
            textAlign: 'center',
          }}
        >
          Daily Progress Timeline
        </Typography>
        
        <Grid container spacing={1}>
          {weekProgress.map((day) => (
            <Grid item xs key={day.date}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 600,
                    mb: 1,
                    display: 'block',
                  }}
                >
                  {day.dayName}
                </Typography>
                <Box
                  sx={{
                    height: 80,
                    bgcolor: day.isToday ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    border: day.isToday ? '2px solid rgba(255, 255, 255, 0.8)' : '1px solid rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    p: 0.5,
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      borderRadius: 1,
                      backgroundColor:
                        day.completionPercentage >= 80 ? '#10b981' :
                        day.completionPercentage >= 60 ? '#f59e0b' :
                        day.completionPercentage >= 40 ? '#f97316' :
                        day.completionPercentage > 0 ? '#ef4444' : 'rgba(255, 255, 255, 0.3)',
                      height: `${Math.max(Math.min(day.completionPercentage, 100), 5)}%`,
                      transition: 'all 0.3s ease-in-out',
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    mt: 1,
                    display: 'block',
                  }}
                >
                  {day.totalMinutes}m
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.7rem',
                  }}
                >
                  {Math.round(day.completionPercentage)}%
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        
        {/* Legend */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
          {[
            { color: '#10b981', label: '80%+ Complete' },
            { color: '#f59e0b', label: '60-79%' },
            { color: '#f97316', label: '40-59%' },
            { color: '#ef4444', label: 'Under 40%' },
          ].map((item) => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: item.color,
                  borderRadius: 1,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.75rem' }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// Weekly Insights Card Component
interface WeeklyInsightsCardProps {
  locking: WeeklyLockingWithTalents;
  progressSummary: ProgressSummary[];
}

const WeeklyInsightsCard: React.FC<WeeklyInsightsCardProps> = ({ locking, progressSummary }) => {
  const totalTarget = locking.total_time_minutes;
  const totalCompleted = progressSummary.reduce((sum, talent) => sum + talent.completed_minutes, 0);
  const overallCompletion = totalTarget > 0 ? (totalCompleted / totalTarget) * 100 : 0;

  const daysWithProgress = progressSummary.length > 0 
    ? progressSummary.reduce((days, talent) => {
        const activeDays = talent.daily_progress.filter(day => day.minutes > 0).length;
        return Math.max(days, activeDays);
      }, 0)
    : 0;

  const bestPerformingTalent = progressSummary.length > 0 
    ? progressSummary.reduce((best, current) => 
        current.completion_percentage > best.completion_percentage ? current : best
      )
    : null;

  const getInsights = () => {
    const insights: string[] = [];
    
    if (overallCompletion >= 90) {
      insights.push("🎉 Outstanding week! You exceeded expectations!");
    } else if (overallCompletion >= 80) {
      insights.push("🌟 Great job! You hit your weekly target!");
    } else if (overallCompletion >= 60) {
      insights.push("👍 Good progress! You're on the right track!");
    } else {
      insights.push("💪 Room for improvement - let's aim higher next week!");
    }
    
    if (daysWithProgress >= 6) {
      insights.push("📅 Excellent consistency - you practiced almost every day!");
    } else if (daysWithProgress >= 4) {
      insights.push("📅 Good consistency - try to practice more days next week!");
    }
    
    if (bestPerformingTalent && bestPerformingTalent.completion_percentage >= 90) {
      insights.push(`🏆 ${bestPerformingTalent.talent_name} was your star performer!`);
    }
    
    return insights;
  };

  const getPerformanceGrade = () => {
    if (overallCompletion >= 90) return { grade: 'A+', color: '#10b981', message: 'Excellent work!' };
    if (overallCompletion >= 80) return { grade: 'A', color: '#10b981', message: 'Great work!' };
    if (overallCompletion >= 70) return { grade: 'B+', color: '#3b82f6', message: 'Good effort!' };
    if (overallCompletion >= 60) return { grade: 'B', color: '#3b82f6', message: 'Solid progress!' };
    if (overallCompletion >= 50) return { grade: 'C+', color: '#f59e0b', message: 'Keep improving!' };
    if (overallCompletion >= 40) return { grade: 'C', color: '#f59e0b', message: 'Room for growth!' };
    return { grade: 'D', color: '#ef4444', message: 'Let\'s do better next week!' };
  };

  const insights = getInsights();
  const performance = getPerformanceGrade();

  return (
    <Grid container spacing={3}>
      {/* Insights Card */}
      <Grid item xs={12} md={8}>
        <Card
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0,
            }}
          />
          <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'white',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              📊 Week Insights
            </Typography>
            
            <Box sx={{ space: 2 }}>
              {insights.map((insight, index) => (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.95)',
                    mb: 2,
                    fontSize: '1rem',
                    lineHeight: 1.6,
                  }}
                >
                  {insight}
                </Typography>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Performance Grade Card */}
      <Grid item xs={12} md={4}>
        <Card
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${performance.color} 0%, ${performance.color}dd 100%)`,
            position: 'relative',
            height: '100%',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0,
            }}
          />
          <CardContent
            sx={{
              p: 4,
              position: 'relative',
              zIndex: 1,
              textAlign: 'center',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'white',
                mb: 2,
              }}
            >
              Performance Grade
            </Typography>
            
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: '4rem',
                lineHeight: 1,
                color: 'white',
                mb: 2,
              }}
            >
              {performance.grade}
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontWeight: 500,
              }}
            >
              {performance.message}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const WeeklyLockInDashboard: React.FC<WeeklyLockInDashboardProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [currentLocking, setCurrentLocking] = useState<WeeklyLockingWithTalents | null>(null);
  const [progressSummary, setProgressSummary] = useState<ProgressSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [streaks, setStreaks] = useState({ currentStreak: 0, longestStreak: 0, totalCompletedWeeks: 0 });
  
  // Modal states
  const [showTalentSelection, setShowTalentSelection] = useState(false);
  const [showWeeklyReport, setShowWeeklyReport] = useState(false);
  
  // UI states
  const [canCreateLockIn, setCanCreateLockIn] = useState(false);
  
  // Reset testing states
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [resetting, setResetting] = useState(false);
  const [resettingProgress, setResettingProgress] = useState(false);

  // Activity reporting modal state
  const [showActivityReporting, setShowActivityReporting] = useState(false);
  const [selectedTalentForReport, setSelectedTalentForReport] = useState<string | null>(null);

  useEffect(() => {
    const initializeDashboard = async () => {
      console.log('🚀 [WeeklyLockInDashboard] Component initialized - starting dashboard initialization');
      console.log('🚀 [WeeklyLockInDashboard] URL when component loaded:', window.location.href);
      await loadDashboardData();
      await checkLockInAvailability();
      console.log('🚀 [WeeklyLockInDashboard] Dashboard initialization complete');
    };
    
    initializeDashboard();
    
    // Set up real-time updates
    const subscription = supabase
      .channel('weekly-lockin-updates')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'weekly_lockings', filter: `user_id=eq.${userId}` },
        () => loadDashboardData()
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'progress_entries' },
        () => loadProgressData()
      )
      .subscribe();

    // Refresh dashboard when user returns to the page (e.g., from talent tree)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👁️ [WeeklyLockInDashboard] Page visibility changed - reloading dashboard data');
        loadDashboardData();
      }
    };

    const handleFocus = () => {
      console.log('🎯 [WeeklyLockInDashboard] Window focused - reloading dashboard data');
      loadDashboardData();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [userId]);

  // Load current user for reset functionality
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setCurrentUser(user);
      } catch (error) {
        console.error('Error getting current user:', error);
      }
    };
    
    getCurrentUser();
  }, []);

  const loadDashboardData = async () => {
    try {
      console.log('🔄 [WeeklyLockInDashboard] Starting to load dashboard data...');
      console.log('🔄 [WeeklyLockInDashboard] Current userId:', userId);
      setLoading(true);
      
      // Load current locking
      console.log('🔄 [WeeklyLockInDashboard] Fetching current locking...');
      const locking = await weeklyLockInService.getCurrentLocking(userId);
      console.log('✅ [WeeklyLockInDashboard] Current locking loaded:', locking);
      setCurrentLocking(locking);
      
      // Load progress if there's an active locking
      if (locking) {
        console.log('🔄 [WeeklyLockInDashboard] Loading progress data for locking:', locking.id);
        await loadProgressData(locking);
      } else {
        console.log('⚠️ [WeeklyLockInDashboard] No active locking found - showing lock-in creation screen');
      }
      
      // Load streaks
      console.log('🔄 [WeeklyLockInDashboard] Loading streaks...');
      const streakData = await weeklyLockInService.calculateStreaks(userId);
      console.log('✅ [WeeklyLockInDashboard] Streaks loaded:', streakData);
      setStreaks(streakData);
      
      console.log('✅ [WeeklyLockInDashboard] Dashboard data loading complete');
    } catch (err) {
      console.error('❌ [WeeklyLockInDashboard] Error loading dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadProgressData = async (locking?: WeeklyLockingWithTalents) => {
    const lockingToUse = locking || currentLocking;
    if (!lockingToUse) return;
    
    try {
      const summary = await weeklyLockInService.getProgressSummary(lockingToUse.id);
      setProgressSummary(summary);
    } catch (err) {
      console.error('Error loading progress data:', err);
    }
  };

  const checkLockInAvailability = async () => {
    try {
      const canCreate = await weeklyLockInService.isInLockInWindow(userId);
      setCanCreateLockIn(canCreate);
    } catch (error) {
      console.error('Error checking lock-in availability:', error);
      // Fallback to allowing lock-in creation
      setCanCreateLockIn(true);
    }
  };

  const handleCreateLocking = async (talents: TalentSelection[], notes?: string) => {
    try {
      const newLocking = await weeklyLockInService.createLocking(userId, talents, notes);
      setCurrentLocking(newLocking);
      setShowTalentSelection(false);
      
      // Refresh all dashboard data to show the locked-in state
      await loadProgressData(newLocking);
      
      // Re-check lock-in availability (should now show locked state)
      await checkLockInAvailability();
      
      // Clear any existing errors
      setError(null);
      
      // Show success message briefly
      setError('🎉 Weekly lock-in created successfully! You can now start reporting your progress.');
      setTimeout(() => setError(null), 4000);
      
    } catch (err) {
      console.error('Error creating locking:', err);
      setError(err instanceof Error ? err.message : 'Failed to create weekly lock-in');
    }
  };

  const handleResetLockIn = async () => {
    if (!currentUser?.email || currentUser.email !== 'amarildo121@gmail.com') {
      setError('Reset functionality is only available for test users');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to reset your current lock-in? This will delete all progress data and allow you to choose new talents. This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      setResetting(true);
      await weeklyLockInService.resetCurrentLockIn();
      
      // Reload all dashboard data
      await loadDashboardData();
      setProgressSummary([]);
      setError(null);
      
      // Show success message briefly
      setError('Lock-in reset successfully! You can now choose new talents.');
      setTimeout(() => setError(null), 3000);
    } catch (err) {
      console.error('Error resetting lock-in:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset lock-in');
    } finally {
      setResetting(false);
    }
  };

  const handleResetProgress = async () => {
    if (!currentUser?.email || currentUser.email !== 'amarildo121@gmail.com') {
      setError('Reset functionality is only available for test users');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to reset your progress for this week? This will delete all activity data but keep your talent selections. This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      setResettingProgress(true);
      
      if (currentLocking) {
        // Delete all progress entries for this locking
        await weeklyLockInService.resetProgress(currentLocking.id);
        
        // Reload progress data
        await loadProgressData(currentLocking);
        setError(null);
        
        // Show success message briefly
        setError('Progress reset successfully! You can now report new activities.');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      console.error('Error resetting progress:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset progress');
    } finally {
      setResettingProgress(false);
    }
  };

  const handleReportActivity = (talentId: string) => {
    setSelectedTalentForReport(talentId);
    setShowActivityReporting(true);
  };

  const handleActivityReported = () => {
    // Reload progress data after activity is reported
    loadProgressData();
    setShowActivityReporting(false);
    setSelectedTalentForReport(null);
  };

  // Talent time per rank mapping (from TalentTree.tsx)
  const TALENT_TIME_PER_RANK: Record<string, number> = {
    'longevity-start': 15, // Sleep Hygiene
    'longevity-cellular': 30, // Zone Two Cardio (30 min per rank)
    'longevity-metabolic': 45, // Strength Training (45 min per rank)
    'longevity-advanced': 25, // Oral Health
    'longevity-zone-five': 30, // Zone Five Cardio
    'longevity-creatine': 10,
    'longevity-energy-balance': 45,
    'longevity-fiber-intake': 30,
    'longevity-protein': 30, // 30 min per rank
    'longevity-time-restricted-eating': 25,
    'longevity-whole-food-eating': 15, // 15 min per rank
    'healthspan-cardio': 60, // Stability Mastery (60 min per rank)
    'healthspan-fitness': 10, // Functional Mobility (10 min per rank)
    'healthspan-flexibility': 15, // Elastic Edge (15 min per rank)
    'healthspan-recovery': 30,
    'healthspan-alcohol': 0, // No time allocation for elimination
    'healthspan-micronutrients': 10,
    'wellness-mindset': 10, // Morning Sun Seeker (10 min per rank)
    'wellness-breathwork': 5, // Guided Breath Work (5 min per rank)
    'wellness-social': 30, // Social Connection (30 min per rank)
    'wellness-purpose': 10, // Purpose & Meaning (10 min per rank)
    'wellness-stress': 10, // Emotional Regulation (10 min per rank)
    'wellness-skincare': 10, // Skin and Hair Maintenance (10 min per rank)
  };

  // Talent max ranks mapping (from TalentTree.tsx)
  const TALENT_MAX_RANKS: Record<string, number> = {
    'longevity-start': 1, // Sleep Hygiene
    'longevity-cellular': 7, // Zone Two Cardio
    'longevity-metabolic': 3, // Strength Training
    'longevity-advanced': 1, // Oral Health
    'longevity-zone-five': 1, // Zone Five Cardio
    'longevity-creatine': 1,
    'longevity-energy-balance': 1,
    'longevity-fiber-intake': 1,
    'longevity-protein': 2,
    'longevity-time-restricted-eating': 1,
    'longevity-whole-food-eating': 2,
    'healthspan-cardio': 3, // Stability Mastery
    'healthspan-fitness': 3, // Functional Mobility
    'healthspan-flexibility': 2, // Elastic Edge
    'healthspan-recovery': 1,
    'healthspan-alcohol': 1, // No time allocation for elimination
    'healthspan-micronutrients': 1,
    'wellness-mindset': 2, // Morning Sun Seeker
    'wellness-breathwork': 2, // Guided Breath Work
    'wellness-social': 1, // Social Connection
    'wellness-purpose': 1, // Purpose & Meaning
    'wellness-stress': 1, // Emotional Regulation
    'wellness-skincare': 1, // Skin and Hair Maintenance
  };

  const getRankBasedTargetMinutes = () => {
    if (!currentLocking) return 0;
    
    return currentLocking.locked_talents.reduce((total, talent) => {
      // Use the actual committed minutes instead of max possible ranks
      return total + talent.target_minutes_per_week;
    }, 0);
  };

  const getRankBasedCompletedMinutes = () => {
    if (!currentLocking || !progressSummary) return 0;
    
    // Simply sum up the actual completed minutes from progress summary
    return progressSummary.reduce((total, talentProgress) => {
      return total + talentProgress.completed_minutes;
    }, 0);
  };

  const getWeekProgress = () => {
    if (!currentLocking) return 0;
    
    // Calculate rank-based target minutes (denominator)
    const totalTarget = getRankBasedTargetMinutes();
    
    // Calculate actual minutes spent based on current tracking card totals (numerator)
    const totalCompleted = getRankBasedCompletedMinutes();
    
    return totalTarget > 0 ? Math.min((totalCompleted / totalTarget) * 100, 100) : 0;
  };

  const formatTimeRemaining = () => {
    if (!currentLocking) return '';
    
    const now = new Date();
    const expires = new Date(currentLocking.expires_at);
    const diffMs = expires.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Expired';
    
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  const getTotalCompletedMinutes = () => {
    return getRankBasedCompletedMinutes();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
          Weekly Talent Lock-In
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Commit to your health journey with weekly talent focus
        </Typography>
      </Box>

      {error && (
        <Box sx={{ 
          bgcolor: 'error.light', 
          border: 1, 
          borderColor: 'error.main', 
          color: 'error.dark', 
          px: 2, 
          py: 1.5, 
          borderRadius: 1,
          mb: 3,
          position: 'relative'
        }}>
          {error}
          <Button 
            onClick={() => setError(null)}
            sx={{ 
              position: 'absolute',
              right: 8,
              top: 8,
              minWidth: 'auto',
              p: 0.5,
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}
          >
            ×
          </Button>
        </Box>
      )}

      {/* Lock-in Status Card */}
      <Box sx={{ mb: 4 }}>
        <Card
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: currentLocking
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' // Red when locked in
              : 'linear-gradient(135deg, #10b981 0%, #059669 100%)', // Green when ready to lock in
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0,
            }}
          />
          <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'white',
                mb: 1.5,
                fontSize: '1.1rem',
              }}
            >
              {currentLocking
                ? '🔒 Locked Into Your Talent Choices'
                : '🚀 Ready to Lock In!'
              }
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1rem',
              }}
            >
              {currentLocking
                ? 'Report progress for the week'
                : 'You can create your weekly lock-in at any time'
              }
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Current Week Information Cards */}
      {currentLocking ? (
        <Box>
          {/* Individual Talent Progress Cards */}
          {currentLocking.locked_talents && currentLocking.locked_talents.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                Individual Talent Progress
              </Typography>
              <Grid container spacing={3}>
                {currentLocking.locked_talents.map((talent) => {
                  const timePerRank = TALENT_TIME_PER_RANK[talent.talent_id] || 1;
                  const maxRanks = TALENT_MAX_RANKS[talent.talent_id] || 1;
                  const talentProgress = progressSummary?.find(p => p.talent_id === talent.talent_id);
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={talent.talent_id}>
                      <WeeklyTalentProgressCard
                        talent={talent}
                        progressSummary={talentProgress}
                        timePerRank={timePerRank}
                        maxRanks={maxRanks}
                        onReportActivity={handleReportActivity}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
            This Week's Progress
          </Typography>
          
          {/* Week Overview and Overall Progress */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <WeekOverviewCard
                startDate={new Date(currentLocking.week_start_date).toLocaleDateString()}
                endDate={new Date(currentLocking.week_end_date).toLocaleDateString()}
                status={currentLocking.status}
                timeRemaining={formatTimeRemaining()}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <OverallProgressCard
                percentage={getWeekProgress()}
                completedMinutes={getTotalCompletedMinutes()}
                totalMinutes={getRankBasedTargetMinutes()}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
            {currentLocking.status === 'pending' && (
              <Button
                variant="contained"
                onClick={() => navigate('/talent-tree?mode=lockin&modify=true')}
                sx={{
                  bgcolor: '#ea580c',
                  '&:hover': { bgcolor: '#c2410c' },
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                }}
              >
                Modify Selection
              </Button>
            )}
            
            {/* Reset Button - Only for test user */}
            {currentUser?.email === 'amarildo121@gmail.com' && currentLocking && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RestartAlt />}
                  onClick={handleResetLockIn}
                  disabled={resetting}
                  sx={{
                    borderColor: '#ef4444',
                    color: '#ef4444',
                    '&:hover': {
                      borderColor: '#dc2626',
                      color: '#dc2626',
                      bgcolor: 'rgba(239, 68, 68, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: '#d1d5db',
                      color: '#9ca3af',
                    },
                  }}
                >
                  {resetting ? 'Resetting...' : 'Reset Lock-In'}
                </Button>
                
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<RestartAlt />}
                  onClick={handleResetProgress}
                  disabled={resettingProgress}
                  sx={{
                    borderColor: '#f59e0b',
                    color: '#f59e0b',
                    '&:hover': {
                      borderColor: '#d97706',
                      color: '#d97706',
                      bgcolor: 'rgba(245, 158, 11, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: '#d1d5db',
                      color: '#9ca3af',
                    },
                  }}
                >
                  {resettingProgress ? 'Resetting...' : 'Reset Progress'}
                </Button>
              </>
            )}
          </Box>

          {/* Detailed Report Cards */}
          {progressSummary && progressSummary.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 4, color: 'white', textAlign: 'center' }}>
                Weekly Analysis
              </Typography>
              
              {/* Overall Stats Cards */}
              <WeeklyStatsCards 
                locking={currentLocking}
                progressSummary={progressSummary}
              />
              
              {/* Daily Timeline Card */}
              <DailyTimelineCard 
                locking={currentLocking}
                progressSummary={progressSummary}
              />
              
              {/* Insights Card */}
              <WeeklyInsightsCard 
                locking={currentLocking}
                progressSummary={progressSummary}
              />
            </Box>
          )}
        </Box>
      ) : (
        /* No Active Locking - Prominent Call to Action Card */
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '50vh',
          px: 2
        }}>
          <Card
            sx={{
              maxWidth: 600,
              width: '100%',
              borderRadius: 4,
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              position: 'relative',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              transform: 'scale(1)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255, 255, 255, 0.1)',
                zIndex: 0,
              }}
            />
            <CardContent sx={{ p: 6, position: 'relative', zIndex: 1, textAlign: 'center' }}>
              {/* Icon */}
              <Box sx={{ mb: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    fontSize: '2rem',
                  }}
                >
                  🚀
                </Avatar>
              </Box>

              {/* Title */}
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  mb: 2,
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                }}
              >
                Ready to Lock In?
              </Typography>

              {/* Description */}
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  lineHeight: 1.5,
                }}
              >
                Select your talents for this week and commit to your health journey. You can create a weekly lock-in at any time!
              </Typography>

              {/* Action Button */}
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/talent-tree?mode=lockin')}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                🎯 Start Weekly Lock-In
              </Button>

              {/* Additional Info */}
              <Box sx={{ 
                mt: 3, 
                p: 3, 
                bgcolor: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  💡 <strong>Tip:</strong> Your weekly schedule runs from Sunday to Sunday, but you can start your lock-in anytime during the week!
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Achievement Streak Cards */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'white', textAlign: 'center' }}>
          Your Achievement Streaks
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StreakCard
              icon={<LocalFireDepartment />}
              title="Current Streak"
              value={streaks.currentStreak}
              description="Keep going! You're on a roll."
              gradientColors={['#f59e0b', '#d97706']}
              iconColor="#f59e0b"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StreakCard
              icon={<EmojiEvents />}
              title="Longest Streak"
              value={streaks.longestStreak}
              description="Your personal best record."
              gradientColors={['#6366f1', '#4f46e5']}
              iconColor="#6366f1"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StreakCard
              icon={<CalendarMonth />}
              title="Total Completed"
              value={streaks.totalCompletedWeeks}
              description="Weeks of consistent progress."
              gradientColors={['#10b981', '#059669']}
              iconColor="#10b981"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Modals */}
      {showTalentSelection && (
        <TalentSelectionModal
          isOpen={showTalentSelection}
          onClose={() => setShowTalentSelection(false)}
          onSubmit={handleCreateLocking}
          existingLocking={currentLocking}
        />
      )}

      {showWeeklyReport && currentLocking && (
        <WeeklyReportModal
          isOpen={showWeeklyReport}
          onClose={() => setShowWeeklyReport(false)}
          locking={currentLocking}
          progressSummary={progressSummary}
        />
      )}

      {showActivityReporting && selectedTalentForReport && currentLocking && (
        <ActivityReportingModal
          isOpen={showActivityReporting}
          onClose={() => setShowActivityReporting(false)}
          talentId={selectedTalentForReport}
          talentName={currentLocking.locked_talents.find(t => t.talent_id === selectedTalentForReport)?.talent_name || ''}
          locking={currentLocking}
          onActivityReported={handleActivityReported}
        />
      )}
    </Container>
  );
};

export default WeeklyLockInDashboard; 